// HolidaysPanel — manage the school's holiday calendar. Visible (read-only)
// to staff, driver, and guardian apps too — this is the one list, not a
// separate "announcement" (see AnnouncementsPanel for one-off pushes).
import { useEffect, useState } from 'react';
import { getHolidays, createHoliday, deleteHoliday } from '../api/client';
import Button from '../ui/Button';
import { Card, CardHeader } from '../ui/Card';

const EMPTY = { date: '', name: '' };

export default function HolidaysPanel() {
  const [holidays, setHolidays] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [msg, setMsg] = useState('');

  useEffect(() => { load(); }, []);

  async function load() {
    try { setHolidays(await getHolidays()); } catch { setMsg('Failed to load holidays'); }
  }

  async function save(e) {
    e.preventDefault();
    try {
      await createHoliday(form);
      setMsg('Holiday added ✅');
      setForm(EMPTY);
      load();
    } catch (err) {
      setMsg('Error: ' + (err.response?.data?.error || err.message));
    }
  }

  async function remove(id) {
    try { await deleteHoliday(id); load(); } catch (err) { setMsg('Error: ' + (err.response?.data?.error || err.message)); }
  }

  const today = new Date().toISOString().slice(0, 10);

  const inputCls = "border border-line rounded-s px-3 py-2 text-sm bg-paper-raised focus:outline-none focus:ring-2 focus:ring-transit mt-1";
  const labelCls = "flex flex-col text-xs font-bold text-ink-soft";

  return (
    <div>
      <h2 className="font-display font-extrabold text-xl text-ink mb-1">Holiday Calendar</h2>
      <p className="text-sm text-ink-soft mb-4">
        Visible to staff, drivers, and guardians in their apps — this is a persistent calendar,
        not a one-off push (use Announcements for that).
      </p>
      {msg && (
        <p className={`text-sm font-bold px-3 py-2 rounded-s mb-4 inline-block ${msg.includes('Error') ? 'bg-critical-tint text-critical' : 'bg-success-tint text-success'}`}>
          {msg}
        </p>
      )}

      <Card className="p-5 mb-6 max-w-[440px] flex flex-col gap-3">
        <form onSubmit={save} className="flex flex-col gap-3">
          <label className={labelCls}>Date
            <input type="date" className={inputCls} required value={form.date} onChange={e => setForm({ ...form, date: e.target.value })} />
          </label>
          <label className={labelCls}>Name
            <input className={inputCls} required value={form.name} onChange={e => setForm({ ...form, name: e.target.value })}
              placeholder="e.g. Independence Day" />
          </label>
          <Button type="submit" className="self-start">Add Holiday</Button>
        </form>
      </Card>

      <Card>
        <CardHeader title={`Holidays (${holidays.length})`} />
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[11px] font-bold uppercase tracking-wider text-ink-soft bg-paper">
                {['Date', 'Name', ''].map(h => <th key={h} className="px-4 py-2.5">{h}</th>)}
              </tr>
            </thead>
            <tbody>
              {holidays.map(h => (
                <tr key={h.id} className="border-t border-line-soft hover:bg-transit-tint/40">
                  <td className={`px-4 py-3 ${h.date >= today ? 'font-bold text-ink' : 'text-ink-soft'}`}>{h.date}</td>
                  <td className="px-4 py-3">{h.name}</td>
                  <td className="px-4 py-3"><Button variant="danger" className="!px-2.5 !py-1 !text-xs" onClick={() => remove(h.id)}>Remove</Button></td>
                </tr>
              ))}
              {!holidays.length && (
                <tr><td className="px-4 py-8 text-center text-ink-soft" colSpan={3}>No holidays added yet.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
